/* HYPE SIDE — Seguir encomenda */
(() => {
  if (customElements.get('hs-tracking')) return;

  const VALID_TRACKING = /^\d{8,20}$/; // envios antigos (Ontime)
  const CTT_CODE = /^[A-Z]{2}\d{9}[A-Z]{2}$/; // códigos CTT, ex.: DS247925769PT
  const cttLink = (code) =>
    `https://appserver.ctt.pt/CustomerArea/PublicArea_Detail?IsFromPublicArea=true&ObjectCodeInput=${encodeURIComponent(code)}&SearchInput=${encodeURIComponent(code)}`;
  const VALID_ORDER = /^#?\s*\d{1,10}$/;
  const VALID_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const MESSAGES = {
    INVALIDO: 'Este número de seguimento não é válido. Os códigos CTT têm 13 caracteres (ex.: DS247925769PT). Confirma o número no email de envio.',
    NAO_ENCONTRADO: 'Não encontrámos nenhum envio com este número. Confirma se está correto. Se a encomenda foi enviada há pouco tempo, pode demorar algumas horas a aparecer.',
    ENCOMENDA_INVALIDA: 'Indica o número da encomenda (ex.: #1234) e o email que usaste na compra.',
    ENCOMENDA_NAO_ENCONTRADA: 'Não encontrámos nenhuma encomenda com estes dados. Confirma o número (está no email de confirmação) e o email usado na compra.',
    INDISPONIVEL: 'O serviço de seguimento está temporariamente indisponível. Tenta novamente dentro de alguns minutos.',
    LIMITE: 'Fizeste muitas pesquisas seguidas. Aguarda um pouco e tenta outra vez.',
  };
  const DATE_FMT = new Intl.DateTimeFormat('pt-PT', { day: 'numeric', month: 'short', year: 'numeric' });

  const tone = (status) => {
    const s = (status || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    if (/devolv|nao entregue|ausente|incid|recusad|anomal|extravi|cancel|tentativa|problema|atrasad/.test(s)) return 'alert';
    if (/entregue/.test(s) && !/transportadora|entregue a ctt/.test(s)) return 'done';
    return 'progress';
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return DATE_FMT.format(new Date(y, m - 1, d));
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  class HsTracking extends HTMLElement {
    connectedCallback() {
      this.api = (this.dataset.api || '').replace(/\/+$/, '');
      this.status = this.querySelector('[data-trk-status]');
      this.orderResult = this.querySelector('[data-trk-order]');
      this.result = this.querySelector('[data-trk-result]');
      this.cttResult = this.querySelector('[data-trk-ctt]');
      this.forms = {
        encomenda: this.querySelector('[data-trk-form="encomenda"]'),
        seguimento: this.querySelector('[data-trk-form="seguimento"]'),
      };
      try {
        this.stageTexts = JSON.parse(this.querySelector('[data-trk-stage-texts]')?.textContent || '{}');
      } catch {
        this.stageTexts = {};
      }

      this.tabs = [...this.querySelectorAll('[data-trk-tab]')];
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.trkTab, true));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const next = this.tabs[(this.tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : this.tabs.length - 1)) % this.tabs.length];
          this.selectTab(next.dataset.trkTab, true);
          next.focus();
        });
      });

      this.forms.encomenda.addEventListener('submit', (e) => {
        e.preventDefault();
        this.searchOrder();
      });
      this.forms.seguimento.addEventListener('submit', (e) => {
        e.preventDefault();
        this.searchTracking(this.querySelector('[data-trk-input]').value);
      });

      const params = new URLSearchParams(location.search);
      const fromEmail = params.get('encomenda');
      if (fromEmail && VALID_ORDER.test(fromEmail)) {
        // Vem do link de um email de atualização: só falta o email do cliente.
        this.forms.encomenda.querySelector('[data-trk-order-input]').value = `#${fromEmail.replace(/^#/, '')}`;
        this.forms.encomenda.querySelector('[data-trk-email-input]').focus({ preventScroll: true });
      }

      const initial = params.get('numero');
      if (initial) {
        this.selectTab('seguimento');
        this.querySelector('[data-trk-input]').value = initial;
        this.searchTracking(initial);
      }
    }

    selectTab(name, clear = false) {
      this.tabs.forEach((tab) => {
        const on = tab.dataset.trkTab === name;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
      });
      Object.entries(this.forms).forEach(([key, form]) => (form.hidden = key !== name));
      if (clear) {
        this.hideResults();
        this.showMessage('', null);
      }
    }

    hideResults() {
      this.orderResult.hidden = true;
      this.result.hidden = true;
      this.cttResult.hidden = true;
    }

    /* ---------- pedidos ---------- */

    async request(path, init, button) {
      this.controller?.abort();
      this.controller = new AbortController();
      this.setLoading(button, true);
      this.hideResults();
      this.showMessage('A procurar…', 'loading');
      try {
        const res = await fetch(this.api + path, { ...init, signal: this.controller.signal });
        let body = null;
        try { body = await res.json(); } catch { /* resposta não-JSON */ }
        if (!res.ok || !body || body.erro) {
          const code = body?.erro?.codigo;
          this.showMessage(MESSAGES[code] || body?.erro?.mensagem || MESSAGES.INDISPONIVEL, 'error');
          return null;
        }
        this.showMessage('', null);
        return body;
      } catch (err) {
        if (err.name !== 'AbortError') this.showMessage(MESSAGES.INDISPONIVEL, 'error');
        return null;
      } finally {
        this.setLoading(button, false);
      }
    }

    async searchOrder() {
      const form = this.forms.encomenda;
      const orderInput = form.querySelector('[data-trk-order-input]');
      const emailInput = form.querySelector('[data-trk-email-input]');
      const numero = orderInput.value.trim();
      const email = emailInput.value.trim();
      const badOrder = !VALID_ORDER.test(numero);
      const badEmail = !VALID_EMAIL.test(email);
      orderInput.setAttribute('aria-invalid', String(badOrder));
      emailInput.setAttribute('aria-invalid', String(badEmail));
      if (badOrder || badEmail) {
        this.hideResults();
        this.showMessage(MESSAGES.ENCOMENDA_INVALIDA, 'error');
        (badOrder ? orderInput : emailInput).focus();
        return;
      }

      // O email vai no corpo do pedido (POST), nunca no URL.
      const data = await this.request('/api/encomenda', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ numero, email }),
      }, form.querySelector('[data-trk-submit]'));
      if (data) this.renderOrder(data);
    }

    async searchTracking(value) {
      const form = this.forms.seguimento;
      const input = form.querySelector('[data-trk-input]');
      const numero = String(value || '').replace(/[\s-]/g, '').toUpperCase();
      if (!VALID_TRACKING.test(numero) && !CTT_CODE.test(numero)) {
        this.hideResults();
        this.showMessage(MESSAGES.INVALIDO, 'error');
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');

      const url = new URL(location.href);
      url.searchParams.set('numero', numero);
      history.replaceState(null, '', url);

      // A CTT não tem API pública: o histórico de um código solto está no site da CTT.
      if (CTT_CODE.test(numero)) {
        this.controller?.abort();
        this.hideResults();
        this.showMessage('', null);
        this.cttResult.querySelector('[data-trk-ctt-number]').textContent = numero;
        this.cttResult.querySelector('[data-trk-ctt-go]').href = cttLink(numero);
        this.cttResult.hidden = false;
        this.reveal(this.cttResult);
        return;
      }

      const data = await this.request(`/api/tracking/${encodeURIComponent(numero)}`, {
        headers: { Accept: 'application/json' },
      }, form.querySelector('[data-trk-submit]'));
      if (data) {
        this.renderTracking(numero, data, false);
        this.reveal(this.result);
      }
    }

    setLoading(button, on) {
      if (button) {
        button.disabled = on;
        button.classList.toggle('is-loading', on);
      }
      this.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    showMessage(text, kind) {
      this.status.textContent = text;
      this.status.hidden = !text;
      this.status.dataset.kind = kind || '';
    }

    reveal(node) {
      if (window.matchMedia('(max-width: 749px)').matches) {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    /* ---------- encomenda ---------- */

    renderOrder(data) {
      const q = (s) => this.orderResult.querySelector(s);
      q('[data-trk-order-title]').textContent = `Encomenda ${data.encomenda || ''}`;

      const status = q('[data-trk-order-status]');
      const text = q('[data-trk-order-text]');
      if (data.cancelada) {
        status.textContent = 'Encomenda cancelada';
        status.dataset.tone = 'alert';
        text.textContent = 'Esta encomenda foi cancelada. Se tiveres dúvidas, fala connosco.';
      } else {
        status.textContent = data.fase?.etiqueta || '—';
        status.dataset.tone = data.fase?.chave === 'entregue' ? 'done' : 'progress';
        text.textContent = this.stageTexts[data.fase?.chave] || '';
      }
      text.hidden = !text.textContent;

      q('[data-trk-order-date]').textContent = formatDate(data.data);
      q('[data-trk-order-tracking]').textContent = data.seguimento || 'Ainda não disponível';
      q('[data-trk-order-carrier]').textContent = data.transportadora || 'CTT';

      // Fases
      const stages = q('[data-trk-stages]');
      stages.replaceChildren();
      q('[data-trk-stages-card]').hidden = Boolean(data.cancelada);
      (data.fases || []).forEach((f) => {
        const li = el('li', `hs-trk__stage is-${f.estado}`);
        if (f.estado === 'atual') li.setAttribute('aria-current', 'step');
        const dot = el('span', 'hs-trk__stage-dot');
        dot.setAttribute('aria-hidden', 'true');
        dot.innerHTML = '<svg viewBox="0 0 24 24" fill="none"><path d="m6 12.5 4 4 8-9" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        const body = el('div', 'hs-trk__stage-body');
        const top = el('div', 'hs-trk__stage-top');
        top.append(el('strong', 'hs-trk__stage-label', f.etiqueta));
        if (f.data) {
          const when = el('time', 'hs-trk__stage-date', formatDate(f.data));
          when.dateTime = f.data;
          top.append(when);
        } else if (f.previsao) {
          const when = el('time', 'hs-trk__stage-date is-forecast', `Previsto: ${formatDate(f.previsao)}`);
          when.dateTime = f.previsao;
          top.append(when);
        }
        body.append(top);
        const srState = { feito: 'concluído', atual: 'fase atual', pendente: 'por fazer' }[f.estado];
        if (srState) body.append(el('span', 'visually-hidden', ` (${srState})`));
        li.append(dot, body);
        stages.append(li);
      });

      // Artigos
      const items = q('[data-trk-items]');
      items.replaceChildren();
      (data.artigos || []).forEach((a) => {
        const li = el('li', 'hs-trk__item');
        const media = el('span', 'hs-trk__item-media');
        if (a.imagem) {
          const img = el('img');
          img.src = a.imagem;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 56;
          img.height = 56;
          media.append(img);
        }
        const info = el('span', 'hs-trk__item-info');
        info.append(el('strong', 'hs-trk__item-title', a.titulo));
        if (a.variante) info.append(el('span', 'hs-trk__item-variant', a.variante));
        li.append(media, info, el('span', 'hs-trk__item-qty', `×${a.quantidade}`));
        items.append(li);
      });
      q('[data-trk-items-card]').hidden = !(data.artigos || []).length;

      // Nota sobre o código de seguimento (ou atraso)
      const note = q('[data-trk-order-note]');
      if (data.atraso && !data.cancelada) {
        note.textContent = this.stageTexts.atraso || '';
        note.hidden = !note.textContent;
      } else if (data.cancelada) {
        note.hidden = true;
      } else if (!data.seguimento && (data.fase?.chave === 'enviada' || data.fase?.chave === 'entregue')) {
        note.textContent = 'A tua encomenda já foi enviada. Se precisares do código de seguimento, fala connosco.';
        note.hidden = false;
      } else if (!data.seguimento) {
        note.textContent = 'Ainda não tens código de seguimento: só existe quando a encomenda é entregue à transportadora. Nessa altura recebes um email e o envio aparece aqui.';
        note.hidden = false;
      } else if (!data.envio) {
        note.textContent = `Código de seguimento: ${data.seguimento}. A informação da transportadora pode demorar algumas horas a aparecer.`;
        note.hidden = false;
      } else {
        note.hidden = true;
      }

      this.orderResult.hidden = false;
      if (data.envio && data.seguimento) {
        this.renderTracking(data.seguimento, data.envio, true);
      }
      this.reveal(this.orderResult);
    }

    /* ---------- envio (transportadora) ---------- */

    renderTracking(numero, data, insideOrder) {
      this.querySelector('[data-trk-result-title]').hidden = !insideOrder;
      const current = this.querySelector('[data-trk-current]');
      current.textContent = data.estadoAtual || data.passo?.etiqueta || '—';
      current.dataset.tone = tone(data.estadoAtual);

      this.querySelector('[data-trk-number]').textContent = numero;
      this.querySelector('[data-trk-carrier]').textContent = data.transportadora || 'Ontime';
      const link = this.querySelector('[data-trk-ctt-link]');
      link.hidden = !data.link;
      if (data.link) link.href = data.link;
      this.querySelector('[data-trk-ontime-row]').hidden = !data.ontime;
      this.querySelector('[data-trk-ontime]').textContent = data.ontime || '';

      const stepNow = Math.min(Math.max(Number(data.passo?.atual) || 1, 1), 5);
      const steps = this.querySelector('[data-trk-steps]');
      steps.style.setProperty('--trk-progress', String((stepNow - 1) / 4));
      steps.querySelectorAll('.hs-trk__step').forEach((li, i) => {
        li.classList.toggle('is-done', i + 1 < stepNow || (stepNow === 5 && i === 4));
        li.classList.toggle('is-current', i + 1 === stepNow);
        if (i + 1 === stepNow) li.setAttribute('aria-current', 'step');
        else li.removeAttribute('aria-current');
      });

      const timeline = this.querySelector('[data-trk-timeline]');
      timeline.replaceChildren();
      const events = Array.isArray(data.eventos) ? data.eventos : [];
      events.forEach((ev, i) => {
        const li = el('li', `hs-trk__event${i === 0 ? ' is-latest' : ''}`);
        li.dataset.tone = tone(ev.estado);
        li.append(el('span', 'hs-trk__event-dot'));
        const body = el('div', 'hs-trk__event-body');
        const top = el('div', 'hs-trk__event-top');
        top.append(el('strong', 'hs-trk__event-status', ev.estado || 'Atualização'));
        const when = el('time', 'hs-trk__event-when', [formatDate(ev.data), ev.hora].filter(Boolean).join(' · '));
        if (ev.data) when.dateTime = ev.hora ? `${ev.data}T${ev.hora}` : ev.data;
        top.append(when);
        body.append(top);
        if (ev.localidade) body.append(el('span', 'hs-trk__event-place', ev.localidade));
        if (ev.descricao) body.append(el('p', 'hs-trk__event-desc', ev.descricao));
        li.append(body);
        timeline.append(li);
      });
      this.querySelector('[data-trk-empty]').hidden = events.length > 0;
      this.result.hidden = false;
    }
  }

  customElements.define('hs-tracking', HsTracking);
})();
